import { ApiService } from "./../../../shared/services/api/api.service";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { CommonModule, formatDate, isPlatformBrowser } from "@angular/common";
import { RouterModule } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Chart, registerables } from "chart.js";
import { catchError, finalize, of, tap } from "rxjs";
import Swal from "sweetalert2";
import { MatDialog } from "@angular/material/dialog";

import { AuthService } from "../../../shared/services/auth.service";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { EmptyComponent } from "../../../shared/components/empty/empty.component";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { NotificationService } from "../../../shared/services/notifications-service";
import { AddReviewComponent } from "../../../shared/add-review/add-review.component";
import { environment } from "../../../../environments/environment";

Chart.register(...registerables);

export interface ChartDataItem {
  name: string;
  value: number;
  color: string;
}

export interface DashboardData {
  success: boolean;
  message: string;
  data: {
    ChartData: ChartData[];
    appointments: Appointment[];
    mostCommonSalons: Salon[];
    mostCommonServices: Service[];
    overview: {
      salonsChart: ChartDataItem[];
      servicesChart: ChartDataItem[];
    };
  };
  totalBooking: number;
  yourReviews: number;
  yourPhotosShared: number;
  totalExpense: number;
}

export interface ChartData {
  label: string;
  revenue: number;
}

export interface Appointment {
  appointment_id: string;
  salon_name: string;
  start_time: string;
  end_time: string;
  status: string;
  services: { name: string; price: number; duration: number }[];
  salon_address?: string;
  salon_city: string;
  salon_images?: [];
  total_price: number;
}

export interface Salon {
  salon_id: string;
  salon_name: string;
  address?: string;
  salon_city: string;
  rating?: number;
  image?: string;
  total_visits: number;
  is_reviewed?: boolean;
}

export interface Service {
  service_id: string;
  service_name: string;
  salon_name?: string;
  salon_address?: string;
  salon_city: string;
  salon_image_url: string;
  price?: string;
  duration?: number;
  total_usage: number;
  staff?: any;
  customer?: any;
}

const fallbackDashboardData: DashboardData = {
  success: false,
  message: "",
  data: {
    ChartData: [],
    appointments: [],
    mostCommonSalons: [],
    mostCommonServices: [],
    overview: {
      salonsChart: [
        {
          name: "No Salon Added Yet",
          value: 1,
          color: "#808080",
        },
      ],
      servicesChart: [
        {
          name: "No Service Added Yet",
          value: 1,
          color: "#808080",
        },
      ],
    },
  },
  totalBooking: 0,
  yourReviews: 0,
  yourPhotosShared: 0,
  totalExpense: 0,
};

@Component({
  selector: "customer-overview",
  standalone: true,
  imports: [CommonModule, RouterModule, LoaderComponent, EmptyComponent],
  templateUrl: "./overview.component.html",
  styleUrl: "./overview.component.scss",
})
export class CustomerOverviewComponent implements AfterViewInit, OnInit {
  @ViewChild("salonsChart") salonsChart!: ElementRef<HTMLCanvasElement>;
  @ViewChild("servicesChart") servicesChart!: ElementRef<HTMLCanvasElement>;

  assetUrl = environment.assetBaseUrl;

  DashboardData: DashboardData = fallbackDashboardData;

  dashboardAppointments: Appointment[] = [];
  dashboardCommonSalons: Salon[] = [];
  dashboardCommonServices: Service[] = [];

  salonsData: ChartDataItem[] = [];
  servicesData: ChartDataItem[] = [];

  salonsChartRef!: Chart;
  servicesChartRef!: Chart;

  userName = "User";
  notificationCount = 0;
  isLoading = true;
  error = "";

  private viewReady = false;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private apiService: ApiService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.authService.getUserDetails().subscribe({
      next: (user) => {
        this.userName = user?.full_name || "User";
      },
      error: () => {
        this.userName = "User";
      },
    });

    this.loadCustomerDashboard();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;

    setTimeout(() => {
      this.refreshSalonsChart();
      this.refreshServicesChart();
    }, 0);
  }

  get totalExpense(): number {
    return Number(this.DashboardData.totalExpense || 0);
  }

  get totalBookingsCount(): number {
    return Number(
      this.DashboardData.totalBooking || this.dashboardAppointments.length || 0,
    );
  }

  get completedBookings(): number {
    return this.dashboardAppointments.filter((appointment) =>
      ["completed", "confirmed"].includes(
        String(appointment.status || "").toLowerCase(),
      ),
    ).length;
  }

  get pendingBookings(): number {
    return this.dashboardAppointments.filter((appointment) =>
      ["pending", "upcoming"].includes(
        String(appointment.status || "").toLowerCase(),
      ),
    ).length;
  }

  get cancelledBookings(): number {
    return this.dashboardAppointments.filter(
      (appointment) =>
        String(appointment.status || "").toLowerCase() === "cancelled",
    ).length;
  }

  get completionRate(): number {
    const total = this.dashboardAppointments.length;

    if (!total) return 0;

    return Math.round((this.completedBookings / total) * 100);
  }

  get cancellationRate(): number {
    const total = this.dashboardAppointments.length;

    if (!total) return 0;

    return Math.round((this.cancelledBookings / total) * 100);
  }

  get averageBookingSpend(): number {
    const total = this.totalBookingsCount || this.dashboardAppointments.length;

    if (!total) return 0;

    return this.totalExpense / total;
  }

  refreshDashboard(): void {
    this.loadCustomerDashboard();
  }

  openNotifications(): void {
    this.notificationService.markAllAsRead();
  }

  private loadCustomerDashboard(): void {
    this.isLoading = true;
    this.error = "";

    this.apiService
      .request<DashboardData>(API_ENDPOINTS.customer.dashboard.detail, "GET")
      .pipe(
        tap((res) => {
          const overview = res?.data?.overview;

          if (!overview) return;

          if (!overview.salonsChart || overview.salonsChart.length === 0) {
            overview.salonsChart = [
              {
                name: "No Salon Added Yet",
                value: 1,
                color: "#C0C0C0",
              },
            ];
          }

          if (!overview.servicesChart || overview.servicesChart.length === 0) {
            overview.servicesChart = [
              {
                name: "No Service Added Yet",
                value: 1,
                color: "#C0C0C0",
              },
            ];
          }
        }),
        catchError((err) => {
          console.error("Error loading dashboard data", err);

          this.error = "Failed to load dashboard data. Please try again.";

          Swal.fire({
            title: "Error",
            text: "Error while retrieving data",
            icon: "error",
            timer: 5000,
            showConfirmButton: false,
            toast: true,
            position: "top-end",
          });

          return of(fallbackDashboardData);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          this.DashboardData = res || fallbackDashboardData;
          this.dashboardAppointments = res?.data?.appointments || [];
          this.dashboardCommonSalons = res?.data?.mostCommonSalons || [];
          this.dashboardCommonServices = res?.data?.mostCommonServices || [];

          this.salonsData = res?.data?.overview?.salonsChart || [];
          this.servicesData = res?.data?.overview?.servicesChart || [];

          if (this.viewReady) {
            setTimeout(() => {
              this.refreshSalonsChart();
              this.refreshServicesChart();
            }, 0);
          }
        },
      });
  }

  refreshAppointments(): void {
    this.loadCustomerDashboard();
  }

  refreshSalonsChart(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.viewReady || !this.salonsChart?.nativeElement) return;

    if (this.salonsChartRef) {
      this.salonsChartRef.destroy();
    }

    const chartData =
      this.salonsData.length > 0
        ? this.salonsData
        : [{ name: "No Data", value: 1, color: "#e5e7eb" }];

    this.salonsChartRef = new Chart(this.salonsChart.nativeElement, {
      type: "doughnut",
      data: {
        labels: chartData.map((item) => item.name),
        datasets: [
          {
            data: chartData.map((item) => item.value),
            backgroundColor: chartData.map((item) => item.color),
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
            },
          },
        },
      },
    });
  }

  refreshServicesChart(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.viewReady || !this.servicesChart?.nativeElement) return;

    if (this.servicesChartRef) {
      this.servicesChartRef.destroy();
    }

    const chartData =
      this.servicesData.length > 0
        ? this.servicesData
        : [{ name: "No Data", value: 1, color: "#e5e7eb" }];

    this.servicesChartRef = new Chart(this.servicesChart.nativeElement, {
      type: "doughnut",
      data: {
        labels: chartData.map((item) => item.name),
        datasets: [
          {
            data: chartData.map((item) => item.value),
            backgroundColor: chartData.map((item) => item.color),
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              usePointStyle: true,
            },
          },
        },
      },
    });
  }

  capitalizedStatus(status: string): string {
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
  }

  statusClass(status: string): string {
    const normalized = String(status || "pending").toLowerCase();

    if (normalized === "completed") return "status-completed";
    if (normalized === "confirmed") return "status-confirmed";
    if (normalized === "cancelled") return "status-cancelled";

    return "status-pending";
  }

  formateDate(dateObj: string, outputFormat = "EEEE, dd MMM"): string {
    if (!dateObj) return "";

    const date = new Date(dateObj);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, outputFormat, "en-US");
  }

  formateTime(dateObj: string): string {
    if (!dateObj) return "";

    const date = new Date(dateObj);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, "hh:mm a", "en-US");
  }

  trackByAppointmentId(index: number, appointment: Appointment): string {
    return `${appointment.appointment_id}-${appointment.start_time}-${appointment.total_price}`;
  }

  onImageError(event: any): void {
    event.target.src = this.assetUrl + "img/mock-salon1.png";
  }

  AddReview(id: string): void {
    const dialogRef = this.dialog.open(AddReviewComponent, {
      width: "500px",
      data: {
        salon_id: id,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadCustomerDashboard();
    });
  }
}